import { BloodworkAnalysis, PanelMarker, RootCauseCategory, Section } from '../../models';
import { NUTRIENT_CATEGORY_ATTRIBUTES } from '../../models/NutrientsAnalysis';
import { markerStatus, Status } from './results-overview-panel';

/*
 * Vitamins & Minerals reads worst-first.
 *
 * Every block in this section's tabs (Supplements above all, since that is the
 * list a practitioner acts on) is about ONE marker, so the blocks can be put in
 * the order the Vitamin & Mineral Insights panel already argues for: Priority,
 * then Support, then Monitor, ties broken by how far the marker sits from its
 * mean. The panel's Focus list and the tab beside it therefore name the same
 * marker first, instead of the tab leading with whatever category happens to be
 * lowest-numbered (Calcium, always, since calculateInsights pushes in
 * marker-name order).
 *
 * Status is never decided here — markerStatus is Results Overview's own
 * per-marker band, the same call NutrientInsights.focusRows makes, so the pill
 * a marker wears and its position in this list cannot drift apart.
 */

// Worst first, matching NutrientInsights' STATUS_ORDER.
const STATUS_ORDER: Status[] = ['red', 'orange', 'yellow', 'green'];

// Sorts after every ranked category: a category whose marker is absent from the
// panel, or that has no entry in NUTRIENT_CATEGORY_ATTRIBUTES at all.
const UNRANKED = -1;

type Ranked<T> = { category: T; index: number; rank: number; magnitude: number };

type CategoryRef = { rootCauseCategory: RootCauseCategory };

/*
 * The rank computation itself, built once over the test's mineral markers and
 * then applied per category. A factory rather than a plain function because
 * both callers rank a whole list, and building the attribute map is the part
 * worth doing once.
 *
 * Returns null when the test has no mineral markers at all: there is nothing to
 * rank by, and both callers treat that as "leave it alone" rather than
 * inventing an order or a status.
 */
function categoryRanker(
  bloodworkAnalysis?: BloodworkAnalysis,
): ((category: CategoryRef) => { rank: number; magnitude: number }) | null {
  const markers = bloodworkAnalysis?.getMineralPanelMarkers?.() ?? [];
  // No markers means no statuses to sort by (the summary panel and the digest
  // can both ask about a section a test has nothing in).
  if (!markers.length) return null;

  const byAttribute = new Map<string, PanelMarker>();
  markers.forEach(pm => byAttribute.set(pm.attribute, pm));

  return (category: CategoryRef): { rank: number; magnitude: number } => {
    const attributes =
      NUTRIENT_CATEGORY_ATTRIBUTES[Number(category.rootCauseCategory)] || [];
    let rank = UNRANKED;
    let magnitude = 0;
    attributes.forEach(attribute => {
      const pm = byAttribute.get(attribute);
      if (!pm) return;
      const status = markerStatus(pm, Section.NUTRIENTS);
      if (!status) return;
      // percentFromMean is already normalized (66.67 = optimal bound, 100 = lab
      // bound), so its magnitude ranks within a color band with no new math —
      // the same tie-break the Focus list uses.
      const distance = Math.abs(pm.percentFromMean ?? 0);
      const index = STATUS_ORDER.indexOf(status);
      // A marker reported in two units is one finding: the more concerning of
      // the two variants speaks for the category.
      if (rank === UNRANKED || index < rank) {
        rank = index;
        magnitude = distance;
      } else if (index === rank) {
        magnitude = Math.max(magnitude, distance);
      }
    });
    return { rank, magnitude };
  };
}

/*
 * The status of each category's own marker, keyed by category id — the SAME
 * band the order below sorts on, handed to callers that need to know how
 * concerning a nutrient pattern is rather than only where it sits in the list.
 *
 * The Top Priority shortlist is the caller. A section whose patterns are one
 * marker each has no citation count to compete on, so it ranks its extra
 * candidates on this instead (see the priority-marker pass in
 * supplements-summary-panel). Sharing the computation is the point: a nutrient
 * the tab lists first cannot be a nutrient the shortlist thinks is mild.
 *
 * A category whose marker the test doesn't carry is absent from the map rather
 * than present with a guessed status.
 */
export function nutrientCategoryStatuses(
  categories: CategoryRef[],
  bloodworkAnalysis?: BloodworkAnalysis,
): Map<number, Status> {
  const statuses = new Map<number, Status>();
  const rankOf = categoryRanker(bloodworkAnalysis);
  if (!rankOf) return statuses;
  categories.forEach(category => {
    const { rank } = rankOf(category);
    if (rank === UNRANKED) return;
    statuses.set(Number(category.rootCauseCategory), STATUS_ORDER[rank]);
  });
  return statuses;
}

export function nutrientCategoryOrder<
  T extends { rootCauseCategory: RootCauseCategory },
>(categories: T[], bloodworkAnalysis?: BloodworkAnalysis): T[] {
  const rankOf = categoryRanker(bloodworkAnalysis);
  // Leave the order alone when there is nothing to rank by.
  if (!rankOf) return categories;

  const ranked: Array<Ranked<T>> = categories.map((category, index) => ({
    category,
    index,
    ...rankOf(category),
  }));

  return ranked
    .sort((a, b) => {
      const aUnranked = a.rank === UNRANKED;
      const bUnranked = b.rank === UNRANKED;
      if (aUnranked !== bUnranked) return aUnranked ? 1 : -1;
      if (aUnranked) return a.index - b.index;
      return (
        a.rank - b.rank || b.magnitude - a.magnitude || a.index - b.index
      );
    })
    .map(r => r.category);
}
